import { useContext } from "react";
import {UserCircleIcon} from "@heroicons/react/24/solid";
import {
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationCircleIcon,
  // MapIcon,
} from "@heroicons/react/24/outline";

import {FormattedMessage} from "react-intl";

import Placeholder from "@ui-components/Placeholder";
import { useWorksiteUsers } from "@hooks/useWorksiteUsers";
import UserNotificationsContext from "../../UserNotificationsContext";
import NotificationPing from "@ui-components/NotificationPing";
import { formatDateStr } from "../../utils/date";

import { TaskStatusBadge } from "./TaskStatusBadge";

function getUserName(id, users) {
  return users.find((u) => u.id === id).name;
}



function truncate(text, length) {
  return text.length > length ? `${text.substring(0, length)}...` : text;
}

/**
 *
 * @param {task} tasks list
 * @param {} worksiteUsers list, it is guaranteed to match the task list because if an user abandons a worksite, the owner inherits the task
 * @returns
 */
export const TaskListing = ({ tasks, worksite, onItemSelectModify }) => {
  const [worksiteUsers] = useWorksiteUsers(worksite);
  const { userNotifications } = useContext(
    UserNotificationsContext
  );

  const hasNotifications = (task) => {
    return userNotifications?.some(
      (n) => n.cod_worksite_task === task.id
    );
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-10 gap-4">
      {tasks.map((task) => {
        const expired = new Date(task.expires_at) < new Date();
        return (
          <div
            key={task.id}
            onClick={() => onItemSelectModify(task.id)}
            className="relative bg-gray-100 border border-gray-200 cursor-pointer rounded-md shadow-lg hover:shadow-2xl p-4 xl:px-3 group:hover transition-all duration-300 max-h-72 flex flex-col gap-2 justify-between flex-shrink-0"
          >
            {
              hasNotifications(task) && (
                <div className="absolute right-1 top-1">
                  <NotificationPing />
                </div>
              )
            }
            <div>
              <div className="flex gap-3 items-start justify-between mb-1">
                <div className="text-lg font-semibold leading-none">{task.des_name}</div>
                <TaskStatusBadge
                  status={task.status}
                />
              </div>
              {!!task.id_assignee && <div className="flex items-center gap-1.5 text-xs">
                <UserCircleIcon className="w-4"/>
                {worksiteUsers.length
                  ? getUserName(task.id_assignee, worksiteUsers)
                  : <Placeholder height="10" width="w-full"/>}
              </div>}
            </div>
            {task.id_survey && (
              <div className="text-xs">
                <span className="font-semibold">
                  <FormattedMessage id="configuration" />
                </span>
                :&nbsp;
                {task?.survey_name || (
                  <FormattedMessage id="unnamed_configuration" />
                )}
              </div>
            )}

            {!!task.des_description && <div className="leading-tight">
              {truncate(task.des_description, 50)}
            </div>}

            <div className="text-xs flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <ClockIcon className="w-3"/>
                <FormattedMessage
                  id="created_on_by"
                  values={{
                    timestamp: formatDateStr(task.created_at),
                    user: worksiteUsers.length
                      ? getUserName(task.id_owner, worksiteUsers)
                      : <Placeholder height="10" width="w-full" classNames="inline" />
                  }}
                />
              </div>
              {task.expires_at && 
                <div className="flex items-center gap-1.5">
                  <ClipboardDocumentCheckIcon className="w-3"/>
                  <p>
                    <FormattedMessage id="deadline" />:&nbsp;
                    <span className={expired ? "text-red-600" : ""}>
                      {expired && <ExclamationCircleIcon className="w-3 inline mr-1 -mt-0.5"/>}
                      <span>{formatDateStr(task.expires_at)}</span>
                    </span>
                  </p>
                </div>
              }
              {/*{task.id_task_map && */}
              {/*  <div className="flex items-center gap-1.5">*/}
              {/*    <MapIcon className="w-3"/>*/}
              {/*    <p>*/}
              {/*      <FormattedMessage id="on_map" />*/}
              {/*    </p>*/}
              {/*  </div>*/}
              {/*}*/}
            </div>

            <div className="text-xxs">
              <p className="text-xs text-gray-700">
                <FormattedMessage id="progress"/>
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 my-1">
                <div
                  className="bg-kerakoll-700 h-full rounded-full"
                  style={{width: `${(task.closed_count ?? 0) / (task.total_count ?? 1) * 100}%`}}
                />
              </div>
              <p>
                <FormattedMessage id="last_update"/>: {formatDateStr(task.modified_at)}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  );
};

export default TaskListing;
